import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { site } from "../data/site";

function NavLink({ href, onClick, children, active, ...rest }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [path, hash] = href.includes("#") ? href.split("#") : [href, ""];
    const targetPath = path || "/";

    const handleClick = (e) => {
        if (onClick) onClick(e);
        if (hash) {
            e.preventDefault();
            if (location.pathname === targetPath) {
                const el = document.getElementById(hash);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            } else {
                navigate(`${targetPath}#${hash}`);
            }
        }
    };

    return (
        <Link
            to={hash ? `${targetPath}#${hash}` : targetPath}
            onClick={handleClick}
            aria-current={active ? "page" : undefined}
            {...rest}
        >
            {children}
        </Link>
    );
}

function isActive(itemHref, pathname) {
    const [base] = itemHref.split("#");
    const target = base || "/";
    if (target === "/") return pathname === "/";
    return pathname === target || pathname.startsWith(target + "/");
}

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
        // Mount-only effect: `onScroll` is declared inside the effect and
        // `setScrolled` is a stable React state setter — neither belongs in
        // the dependency array.
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
    }, [open]);

    return (
        <header
            data-testid="navbar"
            className={`sticky top-0 z-50 w-full bg-white transition-shadow duration-300 ${
                scrolled ? "shadow-[0_4px_18px_rgba(15,40,86,0.08)]" : ""
            }`}
        >
            <div className="container-srm">
                <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-8 lg:gap-12 xl:gap-16 h-[72px] lg:h-[128px]">
                    {/* Left — Logo */}
                    <div className="flex items-center min-w-0 pl-1 lg:pl-2">
                        <Logo variant="dark" size="md" />
                    </div>

                    {/* Center — Primary navigation */}
                    <nav
                        aria-label="Primary"
                        className="hidden lg:flex items-center justify-center min-w-0"
                    >
                        {site.nav.map((item) => {
                            const active = isActive(item.href, pathname);
                            return (
                                <NavLink
                                    key={item.href}
                                    href={item.href}
                                    active={active}
                                    data-testid={`nav-link-${item.label
                                        .toLowerCase()
                                        .replace(/\s+/g, "-")}`}
                                    className={`group relative px-2 lg:px-2 xl:px-3 py-2 text-[11px] lg:text-[11.5px] xl:text-[12px] font-semibold tracking-[0.14em] lg:tracking-[0.16em] xl:tracking-[0.18em] uppercase transition-colors duration-300 whitespace-nowrap ${
                                        active
                                            ? "text-srm-navy"
                                            : "text-srm-navy/80 hover:text-srm-navy"
                                    }`}
                                >
                                    {item.label}
                                    <span
                                        aria-hidden
                                        className={`pointer-events-none absolute left-2 right-2 lg:left-2 lg:right-2 xl:left-3 xl:right-3 bottom-1 h-[2px] origin-left bg-srm-gold transition-transform duration-300 ease-out ${
                                            active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                                        }`}
                                    />
                                </NavLink>
                            );
                        })}
                    </nav>

                    {/* Right — Mobile toggle only (no desktop CTA — Contact is already in the nav; "less selling") */}
                    <div className="flex items-center justify-end min-w-0 pr-1 lg:pr-2">
                        <button
                            type="button"
                            data-testid="nav-mobile-toggle"
                            aria-label="Open menu"
                            onClick={() => setOpen((v) => !v)}
                            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-sm border border-srm-line bg-white text-srm-ink"
                        >
                            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="lg:hidden fixed inset-x-0 top-[72px] bottom-0 z-40 bg-white border-t border-srm-line overflow-y-auto"
                >
                    <nav className="container-srm py-8 flex flex-col gap-1">
                        {site.nav.map((item) => {
                            const active = isActive(item.href, pathname);
                            return (
                                <NavLink
                                    key={item.href}
                                    href={item.href}
                                    active={active}
                                    onClick={() => setOpen(false)}
                                    data-testid={`nav-mobile-link-${item.label
                                        .toLowerCase()
                                        .replace(/\s+/g, "-")}`}
                                    className={`flex items-center justify-between py-4 border-b border-srm-line text-[14px] tracking-[0.14em] uppercase font-semibold ${
                                        active ? "text-srm-gold" : "text-srm-navy"
                                    }`}
                                >
                                    {item.label}
                                    {active && (
                                        <span
                                            aria-hidden
                                            className="ml-2 inline-block h-1.5 w-1.5 rounded-full bg-srm-gold"
                                        />
                                    )}
                                </NavLink>
                            );
                        })}
                        <NavLink
                            href="/contact"
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-cta"
                            className="mt-8 inline-flex items-center justify-center gap-2 rounded-sm bg-srm-navy text-white text-[12px] font-semibold tracking-[0.14em] uppercase px-5 py-4"
                        >
                            Speak with the Team
                        </NavLink>
                    </nav>
                </div>
            )}
        </header>
    );
}
